import { gtmPushAndWait } from '@/lib/gtm-push-and-wait.ts';
import { retryWithBackoff } from '@/lib/retry.ts';
import { AnonymousUser, User } from '@/model/User.ts';
import { useAuthStore } from '@/store/AuthStore.ts';
import { useSettingsStore } from '@/store/SettingsStore';
import axios from 'axios';
import intermind, { Language, SUPPORTED_LANGUAGES, UpdateUserInfoParams, UserInfo, UserSettings } from 'intermind';
import * as jose from 'jose';
import { JWTPayload } from 'jose';
import { nanoid } from 'nanoid';
import qs from 'qs';
import TagManager from '@sooro-io/react-gtm-module';

export interface AuthService {
  authorize(authorizationCode: string, state: string, authReqRedirectUri: string): Promise<void>;
  isAuthorized(): Promise<boolean>;
  refreshAccessToken(): Promise<void>;
  getUser(refreshToken?: boolean): Promise<User | null>;
  getAnonymousUser(): Promise<AnonymousUser | null>;
  login(): void;
  logout(): void;
  getAccessTokenExpiresIn(): number;
  createAnonymousAuthToken(roomId: string, name: string): Promise<string>;
  getUserInfo(): Promise<UserInfo | null>;
  updateUserInfo(updateOptions: UpdateUserInfoParams): Promise<UserInfo | null>;
  deleteUser(): Promise<void>;
  restoreServerUserSettings(): Promise<UserSettings | null>;
}

const JWKS = jose.createRemoteJWKSet(
  new URL(import.meta.env.VITE_OAUTH_PROVIDER_URL + '/protocol/openid-connect/certs'),
  { cacheMaxAge: 600000 } //10 minutes
);

export const ACCESS_TOKEN_KEY = 'access_token';
export const REFRESH_TOKEN_KEY = 'refresh_token';
export const ID_TOKEN_KEY = 'id_token';
export const REDIRECT_AFTER_AUTH_URI_KEY = 'redirect_after_auth_uri';

export const ANONYMOUS_TOKEN_KEY = 'anonymous_token';

export interface JwtPayload extends jose.JWTPayload {
  email: string;
  given_name: string;
  family_name: string;
  resource_access?: {
    vca?: {
      roles: string[];
    };
  };
}

export interface JwtAnonymousPayload extends JwtPayload {
  room_id: string;
  exp: number;
}

export type AuthTokens = {
  access_token: string;
  refresh_token: string;
  id_token: string;
};

export class AuthServiceImpl implements AuthService {
  getAccessToken(): string | null {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  }

  getAnonymousToken(): string | null {
    return localStorage.getItem(ANONYMOUS_TOKEN_KEY);
  }

  async createAnonymousAuthToken(roomId: string, name: string): Promise<string> {
    const anonymousToken = await intermind
      .withOptions({ bypassErrorHandler: true })
      .auth.token.createAnonymous(roomId, name);
    localStorage.setItem(ANONYMOUS_TOKEN_KEY, anonymousToken);
    return anonymousToken;
  }

  private _getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  }

  private _getIdToken(): string | null {
    return localStorage.getItem(ID_TOKEN_KEY);
  }

  async getUser(refreshToken?: boolean): Promise<User | null> {
    try {
      const expIn: number = this.getAccessTokenExpiresIn();
      if (expIn <= 0 || refreshToken) {
        await this.refreshAccessToken();
      }

      const accessToken = this.getAccessToken();
      if (!accessToken) {
        useAuthStore.getState().setUser(null);
        TagManager.dataLayer({ dataLayer: { user_id: null } });
        return null;
      }

      const user = await this._decodeAccessToken(accessToken);
      useAuthStore.getState().setUser(user);
      TagManager.dataLayer({ dataLayer: { user_id: user?.id ?? null } });
      return user;
    } finally {
      useAuthStore.getState().setUserLoading(false);
    }
  }

  async getAnonymousUser(): Promise<AnonymousUser | null> {
    try {
      const expiresIn = this.getAnonymousTokenExpiresIn();
      if (expiresIn <= 0) {
        await this._refreshAnonymousAuthToken();
      }

      const anonymousToken = this.getAnonymousToken();
      if (!anonymousToken) {
        useAuthStore.getState().setAnonymousUser(null);
        return null;
      }

      const anonymousUser = this._decodeAnonymousToken(anonymousToken);
      useAuthStore.getState().setAnonymousUser(anonymousUser);
      return anonymousUser;
    } catch (e) {
      console.error('Error getting the anonymous user', e.message);
      this._clearAnonymousAuthTokens();
      return null;
    }
  }

  async authorize(authorizationCode: string, state: string): Promise<void> {
    // Redirect URI should be the same as in initial request in the login method
    const redirectUri = import.meta.env.VITE_APP_BASE_URL + `/auth/callback`;

    const formData = qs.stringify({
      client_id: `${import.meta.env.VITE_OAUTH_CLIENT_ID}`,
      grant_type: 'authorization_code',
      code: authorizationCode,
      state: state,
      redirect_uri: redirectUri,
    });

    const url = import.meta.env.VITE_OAUTH_PROVIDER_URL + '/protocol/openid-connect/token';
    const { data } = await axios.post(url, formData);
    if (!data.access_token) {
      throw new Error('No access token found in the response');
    }

    this._saveAuthTokens(data);

    await this.trackUserAuthAndWait(data.id_token);

    const localRedirectAfterAuth = localStorage.getItem(REDIRECT_AFTER_AUTH_URI_KEY);
    if (localRedirectAfterAuth) {
      location.href = localRedirectAfterAuth;
      localStorage.removeItem(REDIRECT_AFTER_AUTH_URI_KEY);
    } else {
      location.href = '/';
    }
  }

  async isAuthorized(): Promise<boolean> {
    const user = await this.getUser();
    return !!user;
  }

  private async trackUserAuthAndWait(idToken: string): Promise<void> {
    try {
      const user = await this.getUserInfo();
      if (!user) {
        console.warn('No user info available to track signup/login');
        return;
      }

      const idPayload = jose.decodeJwt(idToken) as JWTPayload;
      const method = idPayload.idp ?? 'email';

      const isSignup =
        user.createdAt &&
        user.lastLogin &&
        // Treat as sign-up if createdAt and lastLogin are within 1 second of each other
        Math.abs(new Date(user.createdAt).getTime() - new Date(user.lastLogin).getTime()) < 1000;

      if (isSignup) await gtmPushAndWait({ event: 'sign_up', method });
      await gtmPushAndWait({ event: 'login', method });
    } catch (err) {
      console.warn('Failed to track signup/login via GTM', err);
    }
  }

  private isTokenExpired(token: string): boolean {
    try {
      const payload = jose.decodeJwt(token) as JwtPayload;
      if (!payload.exp) return true;
      const now = Math.floor(Date.now() / 1000); // Current time in seconds
      return payload.exp <= now;
    } catch (e) {
      console.error('Error decoding the token', e.message);
      return true; // Treat as expired if decoding fails
    }
  }

  async refreshAccessToken(): Promise<void> {
    try {
      const refreshToken = this._getRefreshToken();
      if (!refreshToken) {
        return;
      }

      if (this.isTokenExpired(refreshToken)) {
        throw new Error('The refresh token is expired');
      }

      const formData = qs.stringify({
        client_id: `${import.meta.env.VITE_OAUTH_CLIENT_ID}`,
        grant_type: 'refresh_token',
        refresh_token: refreshToken,
      });

      const refreshOperation = async () => {
        const url = import.meta.env.VITE_OAUTH_PROVIDER_URL + '/protocol/openid-connect/token';
        const { data } = await axios.post(url, formData);
        if (!data.access_token) {
          throw new Error('No access token found in the response');
        }

        return data;
      };

      const data = await retryWithBackoff(refreshOperation);
      this._saveAuthTokens(data);
    } catch (e) {
      console.error('Error refreshing the access token', e.message);
      this._clearAuthTokens();
    }
  }

  async _refreshAnonymousAuthToken(): Promise<void> {
    const anonymousAuthToken = this.getAnonymousToken();
    if (!anonymousAuthToken) {
      return;
    }

    const newAnonymousToken = await intermind.auth.token.refreshAnonymous(anonymousAuthToken);
    localStorage.setItem(ANONYMOUS_TOKEN_KEY, newAnonymousToken);
  }

  private async _decodeAccessToken(accessToken: string): Promise<User | null> {
    if (!accessToken) {
      return null;
    }

    const payload = await this._getValidAccessTokenPayload(accessToken);
    if (!payload) {
      return null;
    }

    return this._createUserFromPayload(payload, accessToken);
  }

  private async _getValidAccessTokenPayload(accessToken: string): Promise<JwtPayload | null> {
    try {
      return await this._verifyAccessToken(accessToken);
    } catch (e) {
      console.error('Error decoding the access token. Attempting to refresh...', e.message);
      return await this._handleTokenRefresh();
    }
  }

  private async _handleTokenRefresh(): Promise<JwtPayload | null> {
    try {
      await this.refreshAccessToken();
      const newAccessToken = this.getAccessToken();
      if (!newAccessToken) {
        throw new Error('No access token available after refresh');
      }
      return await this._verifyAccessToken(newAccessToken);
    } catch (e) {
      console.error('Failed to refresh and decode access token', e.message);
      this._clearAuthTokens();
      return null;
    }
  }

  private _createUserFromPayload(payload: JwtPayload, accessToken: string): User | null {
    if (!payload?.sub || !payload?.email) {
      console.error('Invalid payload: missing required fields');
      return null;
    }

    const isAdmin = payload.resource_access?.vca?.roles?.includes('admin') ?? false;
    return new User(payload.sub, payload.email, payload.given_name, payload.family_name, accessToken, isAdmin);
  }

  private async _verifyAccessToken(accessToken: string): Promise<JwtPayload | null> {
    if (!accessToken) {
      return null;
    }

    const { payload } = await jose.jwtVerify(accessToken, JWKS, {
      algorithms: ['RS256'],
      issuer: import.meta.env.VITE_OAUTH_PROVIDER_URL,
    });
    return payload as JwtPayload;
  }

  private _decodeAnonymousToken(anonymousToken: string): AnonymousUser | null {
    if (!anonymousToken) {
      return null;
    }

    let payload: JwtAnonymousPayload;
    try {
      payload = jose.decodeJwt<JwtAnonymousPayload>(anonymousToken);
    } catch (e) {
      console.error('Error decoding the anonymous token.', e.message);
      return null;
    }

    if (!payload?.sub || !payload?.room_id || !payload?.exp) {
      console.error('No sub, room_id or exp found in the payload');
      return null;
    }

    return new AnonymousUser(payload.sub, payload.given_name, payload.room_id, payload.exp, anonymousToken);
  }

  login(redirectAfterAuthUri?: string, locale?: string): void {
    localStorage.setItem(REDIRECT_AFTER_AUTH_URI_KEY, redirectAfterAuthUri || location.pathname + location.search);

    location.href =
      import.meta.env.VITE_OAUTH_PROVIDER_URL +
      '/protocol/openid-connect/auth' +
      `?client_id=${import.meta.env.VITE_OAUTH_CLIENT_ID}` +
      '&scope=openid' +
      '&response_type=code' +
      `&state=${nanoid()}` +
      `&redirect_uri=${encodeURIComponent(import.meta.env.VITE_APP_BASE_URL + `/auth/callback`)}` +
      `&ui_locales=${locale || useSettingsStore.getState().language || 'en'}`;
  }

  logout(): void {
    const idToken = this._getIdToken();
    this._clearAuthTokens();
    if (idToken) {
      location.href =
        import.meta.env.VITE_OAUTH_PROVIDER_URL +
        '/protocol/openid-connect/logout' +
        `?id_token_hint=${idToken}` +
        `&post_logout_redirect_uri=${import.meta.env.VITE_APP_BASE_URL}`;
    }
  }

  getAccessTokenExpiresIn(): number {
    const accessToken = this.getAccessToken();
    if (!accessToken) {
      return 0;
    }

    const payload = jose.decodeJwt(accessToken) as JwtPayload;
    if (!payload.exp) {
      return 0;
    }

    return payload.exp * 1000 - Date.now();
  }

  private getAnonymousTokenExpiresIn(): number {
    const anonymousToken = this.getAnonymousToken();
    if (!anonymousToken) {
      return 0;
    }

    const payload = jose.decodeJwt(anonymousToken) as JwtAnonymousPayload;
    return payload.exp * 1000 - Date.now();
  }

  private _saveAuthTokens(tokens: AuthTokens) {
    localStorage.setItem(ACCESS_TOKEN_KEY, tokens.access_token);
    localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refresh_token);
    localStorage.setItem(ID_TOKEN_KEY, tokens.id_token);
  }

  private _clearAuthTokens() {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(ID_TOKEN_KEY);
    useAuthStore.getState().setUser(null);
    TagManager.dataLayer({ dataLayer: { user_id: null } });
  }

  private _clearAnonymousAuthTokens() {
    localStorage.removeItem(ANONYMOUS_TOKEN_KEY);
    useAuthStore.getState().setAnonymousUser(null);
  }

  async getUserInfo(): Promise<UserInfo | null> {
    return await intermind.auth.user.get();
  }

  async updateUserInfo(updateOptions: UpdateUserInfoParams): Promise<UserInfo | null> {
    try {
      const user = await this.getUser();
      if (!user) {
        return null;
      }
      return await intermind.auth.user.update(updateOptions);
    } catch (e) {
      console.error('Error updating user info', e.message);
      return null;
    }
  }

  async restoreServerUserSettings(): Promise<UserSettings | null> {
    const user = await this.getUser();
    if (!user) {
      return null;
    }

    const userInfo = await this.getUserInfo();
    if (!userInfo) {
      return null;
    }

    const userSettings = userInfo.settings;
    if (!userSettings) {
      return null;
    }

    if (userSettings.layout) {
      useSettingsStore.getState().setLayout(userSettings.layout);
    }

    if (userSettings.voiceAssistantProvider) {
      useSettingsStore.getState().setVoiceAssistantProvider(userSettings.voiceAssistantProvider);
    }

    if (userSettings.language) {
      // If the user has a specific language set, use it
      useSettingsStore.getState().setLanguage(userSettings.language);
    } else {
      // Otherwise, try to use browser language
      const browserLang = navigator.language.split('-')[0].toUpperCase();
      if (SUPPORTED_LANGUAGES.includes(browserLang as Language)) {
        useSettingsStore.getState().setLanguage(browserLang as Language);
      } else {
        // Set English as default if browser language is not supported
        useSettingsStore.getState().setLanguage(Language.ENGLISH);
      }
    }

    return userSettings;
  }

  async deleteUser(): Promise<void> {
    try {
      const user = await this.getUser();
      if (!user) {
        throw new Error('User not found');
      }

      await intermind.auth.user.delete();
      this._clearAuthTokens();
      window.location.href = '/';
    } catch (e) {
      console.error('Error deleting user', e.message);
      throw e;
    }
  }
}

export const authService = new AuthServiceImpl();
